import React, { useState, useEffect } from "react";
import { getGalleryImages } from '../dataManager';
import aboutus from "../assets/gallery_hero.png";
import useBidirectionalScroll from '../hooks/useBidirectionalScroll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import Loader from './Loader';

export default function Gallery({ navigateTo }) {
  useBidirectionalScroll('.gallery-page-container', 120);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5
      });
    };
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryImages] = useState(() => getGalleryImages());

  return (
    <div className="gallery-page-container"
      style={{
        minHeight: "100vh",
        background: "var(--bg-primary)",
      }}
    >
      <section className="hero" style={{ position: 'relative', overflow: 'hidden' }}>
        {/* 3D Interactive Parallax Background Typography */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 0,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}>
          <div style={{
            fontSize: 'min(20vw, 250px)',
            fontWeight: 900,
            color: 'transparent',
            WebkitTextStroke: '2px rgba(10, 102, 255, 0.08)',
            letterSpacing: '10px',
            transform: `translate(${mousePos.x * 50}px, ${mousePos.y * 50}px)`,
            transition: 'transform 0.1s ease-out',
            whiteSpace: 'nowrap',
            userSelect: 'none'
          }}>
            GALLERY
          </div>
        </div>

        {/* Ambient Grid Overlay */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'linear-gradient(rgba(10, 102, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(10, 102, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          zIndex: 1,
          pointerEvents: 'none',
          transform: `translate(${mousePos.x * 20}px, ${mousePos.y * 20}px)`,
          transition: 'transform 0.15s ease-out',
        }}></div>

        <div className="hero-background-slider" style={{ zIndex: 2, transform: `translate(${mousePos.x * -30}px, ${mousePos.y * -30}px)`, transition: 'transform 0.2s ease-out' }}>
          <div className="full-slide active">
            <div className="bg-img-container" style={{ position: 'absolute', inset: 0 }}>
              <img src={aboutus} alt="Gallery Header" className="bg-img" style={{ objectPosition: 'right center' }} />
            </div>
            <div className="bg-overlay"></div>
          </div>
        </div>

        <div className="hero-content" style={{ zIndex: 10 }}>
          <button className="outline-button" style={{ marginBottom: '2rem' }} onClick={() => navigateTo('home')}>
            &#8592; Back to Home
          </button>
          <h1 className="hero-title">Gallery</h1>
          <p className="hero-description">
            <strong style={{ color: "var(--accent-color)" }}>Manufacturing Excellence</strong><br /><br />
            Explore our state-of-the-art manufacturing facilities, precision machinery, and high-performance industrial equipment.
          </p>
        </div>
      </section>

      {/* Image Gallery */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "30px",
          padding: "6rem 4rem 2rem"
        }}
      >
        {galleryImages.map((img, index) => (
          <div
            key={index}
            className={`gallery-item-card animate-hidden animate-scale delay-${(index % 4) * 100}`}
            style={{
              overflow: "hidden",
              borderRadius: "16px",
              cursor: "pointer",
              position: "relative",
              boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
              aspectRatio: '1'
            }}
            onClick={() => setSelectedImage(img)}
          >
            <img
              src={img}
              alt="gallery"
              onError={(e) => { 
                e.target.onerror = null; 
                e.target.src = 'https://placehold.co/600x600/0a66ff/ffffff?text=Gallery+Image'; 
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.6s var(--ease-out-expo)",
              }}
            />
            <div className="gallery-item-hover-icon" style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(10, 102, 255, 0.2)',
              opacity: 0,
              transition: 'opacity 0.4s ease'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                background: '#fff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
              }}>
                <FontAwesomeIcon icon={faEye} style={{ color: 'var(--accent-color)', fontSize: '1.2rem' }} />
              </div>
            </div>
            <div
              style={{
                position: "absolute",
                bottom: "0",
                width: "100%",
                padding: "2rem 1.5rem 1rem",
                background: "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                color: '#fff'
              }}
            >
              <p style={{ margin: 0, fontSize: "0.85rem", textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Industrial Snapshot
              </p>
            </div>
          </div>
        ))}
      </div>


      {selectedImage && (
        <div
          onClick={() => setSelectedImage(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999,
          }}
        >
          <img
            src={selectedImage}
            alt="preview"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "12px",
            }}
          />
        </div>
      )}

    </div>
  );
}